import { useMutation, useQuery } from "@tanstack/react-query";
import { produce } from "immer";

import {
  CopyExamDataInput,
  CreateExamConfigData,
  deserialize,
  gql,
  GraphQLAPI,
  GraphQLQuery,
  GraphQLResult,
  parseItemCanvas,
  queries,
} from "@examspark/api";
import { formatResourceIdFieldValue } from "@examspark/api-ddb";
import { getTagFromResource, SystemTag } from "@examspark/common";
import {
  CreateResourceInput,
  IDSK,
  ParsedExam,
  Replace,
  Resource,
  SearchableConnection,
  SearchableResourceConnection,
  SearchableResourceFilterInput,
  Student,
  Tag,
  UpdateResourceInput,
} from "@examspark/types";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";
import { usePaginatedSearch } from "../../hooks/usePaginatedSearch";
import { useAppContext } from "../../providers/AppContext";

export const useMutationCreateExamConfig = () => {
  return useMutation({
    mutationFn: async (params: {
      input: CreateExamConfigData;
      options?: {
        createAccessCode?: boolean;
      };
    }) => {
      const result = await gql.operations.exam.createExamConfig(params.input, {
        createAccessCode: params.options?.createAccessCode,
      });
      return result.data?.createResource;
    },
  });
};

export const useMutationCreateExam = () => {
  return useMutation({
    mutationFn: async (input: CreateResourceInput) => {
      const result = (await GraphQLAPI.query({
        query: queries.resource.createResource({
          label: "CreateExam",
        }),
        variables: {
          input,
        },
      })) as GraphQLResult<{ createResource: Resource }>;

      return result.data?.createResource;
    },
  });
};

export const useMutationCopyExam = () => {
  const [state] = useAppContext();

  return useMutation({
    mutationFn: async (params: {
      examKey: IDSK;
      examData?: Partial<{
        name: string;
        tags: Tag[];
      }>;
      onBeforeCopy?: (exam: CopyExamDataInput) => CopyExamDataInput;
    }) => {
      const exam = params.examKey;
      const appId = state.appLicense?.appId;
      const appKey = state.appLicense?.appKey;
      const workspaceId = state.workspaceUser?.workspace?.id;
      const bankId = state.appLicenseConfig?.userContentBankId;
      const creatorId = state.user?.id;

      if (!workspaceId) {
        console.error("No workspaceId found");
        return;
      }
      if (!creatorId) {
        console.error("No creatorId found");
        return;
      }
      if (!bankId) {
        console.error("No bankId found");
        return;
      }

      let newExamData: CopyExamDataInput = {
        workspaceId,
        creatorId,
        bankId,
        name: params.examData?.name ?? undefined,
        tags: [
          {
            key: SystemTag.OriginExamId,
            type: "string",
            value: exam.id,
          },
          ...(params.examData?.tags ?? []),
        ],
      };

      if (params.onBeforeCopy) {
        newExamData = params.onBeforeCopy(newExamData);
      }

      if (appId) {
        if (!newExamData.tags) newExamData.tags = [];
        newExamData.tags.push({
          key: "app-id",
          type: "string",
          value: appId,
        });
      }
      if (appKey) {
        if (!newExamData.tags) newExamData.tags = [];
        newExamData.tags.push({
          key: "app-key",
          type: "string",
          value: appKey,
        });
      }

      const copyExamResult = await gql.operations.exam.copyExam(
        exam,
        newExamData,
      );

      if (!copyExamResult) {
        console.error("No examConfig found");
        return;
      }

      return {
        exam: copyExamResult.data?.createResource,
      };
    },
  });
};

export const useMutationUpdateExam = () => {
  return useMutation({
    mutationFn: async (input: UpdateResourceInput) => {
      const result = (await GraphQLAPI.query({
        query: queries.resource.updateResource({
          label: "UpdateExam",
        }),
        variables: {
          input,
        },
      })) as GraphQLResult<{ updateResource: Resource }>;

      return result.data?.updateResource;
    },
  });
};

export const useQuerySearchExams = (
  params: {
    filter?: SearchableResourceFilterInput;
  } = {},
) => {
  const authorizationContext = useAuthorizationContext();

  return usePaginatedSearch({
    queryKey: ["exams", params.filter],
    limit: 10,
    queryFn: async (context) => {
      const filter: SearchableResourceFilterInput = {
        sk: {
          eq: "exam#v0",
        },
        isDeleted: {
          ne: true,
        },
        ...params.filter,
      };

      const result = (await GraphQLAPI.query({
        query: queries.search.search({
          fields:
            "__typename id sk name maturity bankId createdAt updatedAt numberLabel tags { key value } resourcesIds { id } workspaceId visibility",
          label: "SearchExams",
        }),
        variables: {
          sort: [
            {
              field: "updatedAt",
              direction: "desc",
            },
          ],
          filter,
          authorizationContext,
          ...context.meta.variables,
        },
      })) as GraphQLResult<{ search: SearchableConnection<Resource> }>;

      console.log("result", result);
      return result.data?.search;
    },
  });
};

export const useQueryGetExamConfigCountForExamId = (params: {
  examId?: string;
}) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    enabled: !!params.examId,
    queryKey: ["exam-configs", "count-for-exam", params.examId],
    queryFn: async () => {
      if (!params.examId) return;

      const filter: SearchableResourceFilterInput = {
        sk: {
          eq: "exam-config",
        },
        resourceId: {
          eq: formatResourceIdFieldValue({
            id: params.examId,
            sk: `exam#v0`,
          }),
        },
      };

      const result = (await GraphQLAPI.query({
        query: queries.search.search({
          fields: "__typename id sk ",
          label: "GetExamConfigCountForExamId",
        }),
        variables: {
          limit: 0,
          sort: [
            {
              field: "updatedAt",
              direction: "desc",
            },
          ],
          filter,
          authorizationContext,
        },
      })) as GraphQLResult<{
        search: Pick<SearchableResourceConnection, "total">;
      }>;

      console.log("result", result);
      return result.data?.search;
    },
  });
};

/** List all copies of a given exam. */
export const useQuerySearchCopiedExams = (params: { examId?: string }) => {
  const [state] = useAppContext();
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    enabled: !!params.examId,
    queryKey: ["copied-exams", params.examId, state.user?.id],
    queryFn: async () => {
      const filter: SearchableResourceFilterInput = {
        sk: {
          eq: "exam#v0",
        },
        isDeleted: {
          ne: true,
        },
        creatorId: {
          eq: state.user?.id,
        },
        tags: {
          eq: `${SystemTag.OriginExamId}#${params.examId}`,
        },
      };

      const result = (await GraphQLAPI.query({
        query: queries.search.search({
          fields:
            "__typename id sk name maturity bankId updatedAt numberLabel tags { key value } resourcesIds { id }",
          label: "SearchExams",
        }),
        variables: {
          sort: [
            {
              field: "updatedAt",
              direction: "desc",
            },
          ],
          filter,
          authorizationContext,
        },
      })) as GraphQLResult<{ search: SearchableResourceConnection }>;

      console.log("result", result);
      return result.data?.search;
    },
  });
};

export const useQueryGetExam = (
  params: Replace<Partial<IDSK>, { sk: `exam#v${number}` }>,
) => {
  return useQuery({
    enabled: !!params.id,
    queryKey: ["exam", params],
    queryFn: () => {
      if (!params.id) return;
      return gql.operations.exam.getExam({
        id: params.id,
        sk: params.sk,
      });
    },
  });
};

export const useQueryGetPlayableExam = (
  params: Replace<Partial<IDSK>, { sk: `exam#v${number}` }>,
) => {
  return useQuery({
    enabled: !!params.id,
    queryKey: ["playable-exam", params],
    queryFn: () => {
      if (!params.id) return;
      return gql.operations.exam.getPlayableExam({
        id: params.id,
        sk: params.sk,
      });
    },
    select: (data) => {
      return produce(data, (draft) => {
        draft?.questions?.forEach((question) => {
          parseItemCanvas(question);
        });
      }) as ParsedExam | undefined;
    },
  });
};

export const useMutationArchiveExam = () => {
  const authorizationContext = useAuthorizationContext();

  return useMutation({
    mutationFn: async (input: IDSK) => {
      const result = await GraphQLAPI.query<GraphQLQuery<void>>({
        query: queries.exam.archiveExam,
        variables: {
          authorizationContext,
          input,
        },
      });

      if (result.errors) {
        throw new Error(result.errors.map((e) => e.message).join("\n\n"));
      }
    },
  });
};

export const useQueryGetExamConfig = (
  params: Replace<Partial<IDSK>, { sk: "exam-config" }>,
) => {
  return useQuery({
    enabled: !!params.id,
    queryKey: ["exam-config", params],
    queryFn: () => gql.operations.exam.getExamConfig(params),
  });
};

export const useMutationGetExamTicket = () => {
  return useMutation({
    mutationFn: async (params: { code: string; studentId?: string }) => {
      // fetch exam ticket by access code
      const examTicket = await gql.operations.exam.getExamTicket(params);
      if (!examTicket) return;

      if (
        examTicket?.resource?.config &&
        typeof examTicket.resource.config === "string"
      ) {
        examTicket.resource.config = JSON.parse(examTicket.resource.config);
      }
      if (examTicket?.resource?.resource) {
        examTicket.resource.resource = deserialize.parseExam(
          examTicket.resource.resource,
        );
      }

      // get classroomId from access-code to fetch student...if studentId is passed
      let student: Student | undefined;
      if (params.studentId) {
        const classroomId = getTagFromResource(
          examTicket.resource,
          "cohort-classroom",
        )?.value;

        if (classroomId) {
          // we need to lookup case-(in)sensitive variations since either could
          // have been used to encode the student Id. [2025-05-26] This will
          // ultimately not be necessary as we're transitioning to all
          // case-insensitive uuids
          const caseSensitiveStudentPromise =
            gql.operations.exam.getClassroomStudent({
              classroomId,
              studentId: params.studentId,
              caseSensitive: true,
            });
          const caseInsensitiveStudentPromise =
            gql.operations.exam.getClassroomStudent({
              classroomId,
              studentId: params.studentId,
              caseSensitive: false,
            });

          const [caseSensitiveStudent, caseInsensitiveStudent] =
            await Promise.all([
              caseSensitiveStudentPromise,
              caseInsensitiveStudentPromise,
            ]);

          student = caseSensitiveStudent ?? caseInsensitiveStudent ?? undefined;
        }
      }

      return {
        ticket: examTicket,
        student: student,
      };
    },
  });
};
